import React, { useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { FileEdit, Plus, Users, Clock, Check } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { levelDisplayName } from "@/lib/levelLabels";
import { DatePicker } from "@/components/ui/calendar";

export default function TeacherHomework() {
  const { t, language } = useI18n();
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["homework-teacher"],
    queryFn: async () => unwrap(await api.get("/homework")),
  });
  const { data: home } = useQuery({
    queryKey: ["teacher-home"],
    queryFn: async () => unwrap(await api.get("/teacher/home")),
  });
  const { data: subjects } = useQuery({
    queryKey: ["subjects"],
    queryFn: async () => unwrap(await api.get("/subjects")),
  });
  const qc = useQueryClient();

  const createMut = useMutation({
    mutationFn: async (form) => unwrap(await api.post("/homework", form)),
    onSuccess: () => {
      toast.success(t("teacher.homeworkPostedToast"));
      refetch();
      setOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-5" data-testid="teacher-homework">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">
            {t("teacher.homeworkTitle")}
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {t("teacher.homeworkSubtitle")}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white h-10"
              data-testid="new-hw-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> {t("teacher.new")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">
                {t("teacher.postHomework")}
              </DialogTitle>
            </DialogHeader>
            <NewHomeworkForm
              sections={home?.sections || []}
              subjects={subjects || []}
              onSubmit={(f) => createMut.mutate(f)}
              loading={createMut.isPending}
              t={t}
            />
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="space-y-3 stagger">
          {(data || []).map((hw) => {
            const overdue = hw.due_date < today;
            return (
              <Card
                key={hw.id}
                className="border-[var(--subtle)] lift-card cursor-pointer"
                onClick={() => setDetailId(hw.id)}
                data-testid={`hw-${hw.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-md bg-[var(--brand)]/10 text-[var(--brand)] flex items-center justify-center flex-shrink-0">
                      <FileEdit className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">
                        {hw.subject?.name}
                      </div>
                      <div className="font-semibold text-[var(--text-primary)] mt-0.5">
                        {hw.title}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 text-xs">
                        <Clock className="h-3 w-3 text-[var(--text-muted)]" />
                        <span
                          className={
                            overdue
                              ? "text-[var(--error)] font-semibold"
                              : "text-[var(--text-muted)]"
                          }
                        >
                          {t("teacher.dueOn", {
                            date: formatDate(hw.due_date),
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {(data || []).length === 0 && (
            <Card className="border-[var(--subtle)]">
              <CardContent className="py-10 text-center text-[var(--text-muted)]">
                <FileEdit className="h-8 w-8 mx-auto mb-2 opacity-40" />{" "}
                {t("teacher.noHomeworkYet")}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Dialog open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detailId && (
            <HomeworkDetail hid={detailId} onGraded={() => refetch()} t={t} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewHomeworkForm({ sections, subjects, onSubmit, loading, t }) {
  const [form, setForm] = useState({
    section_id: sections[0]?.id || "",
    subject_id: "",
    title: "",
    description: "<p></p>",
    due_date: new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10),
    max_marks: 10,
  });
  React.useEffect(() => {
    if (sections[0])
      setForm((f) => (f.section_id ? f : { ...f, section_id: sections[0].id }));
  }, [sections]);
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase">{t("teacher.section")}</Label>
        <Select
          value={form.section_id}
          onValueChange={(v) => setForm({ ...form, section_id: v })}
        >
          <SelectTrigger className="mt-1" data-testid="hw-section">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sections.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {levelDisplayName(s.grade, language)} · {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase">{t("teacher.subject")}</Label>
        <Select
          value={form.subject_id}
          onValueChange={(v) => setForm({ ...form, subject_id: v })}
        >
          <SelectTrigger className="mt-1" data-testid="hw-subject">
            <SelectValue placeholder="Subject" />
          </SelectTrigger>
          <SelectContent>
            {subjects.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase">{t("teacher.title")}</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="mt-1"
          data-testid="hw-title"
        />
      </div>
      <div>
        <Label className="text-xs uppercase">{t("teacher.instructions")}</Label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="mt-1 min-h-24 text-sm"
          data-testid="hw-description"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase">{t("teacher.dueDate")}</Label>
          <DatePicker
            value={form.due_date}
            onChange={(date) => setForm({ ...form, due_date: date })}
          />
        </div>
        <div>
          <Label className="text-xs uppercase">{t("teacher.maxMarks")}</Label>
          <Input
            type="number"
            value={form.max_marks}
            onChange={(e) =>
              setForm({ ...form, max_marks: Number(e.target.value) })
            }
            className="mt-1 font-mono-num"
          />
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={() => onSubmit(form)}
          disabled={
            !form.section_id || !form.subject_id || !form.title || loading
          }
          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
          data-testid="submit-hw"
        >
          {loading ? t("teacher.posting") : t("teacher.postHomework")}
        </Button>
      </DialogFooter>
    </div>
  );
}

function HomeworkDetail({ hid, onGraded, t }) {
  const { data, refetch } = useQuery({
    queryKey: ["hw", hid],
    queryFn: async () => unwrap(await api.get(`/homework/${hid}`)),
  });
  if (!data) return <Skeleton className="h-64" />;
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="font-display">{data.title}</DialogTitle>
      </DialogHeader>
      <div className="text-xs text-[var(--text-muted)]">
        {data.subject?.name} ·{" "}
        {t("teacher.dueOn", { date: formatDate(data.due_date) })}
      </div>
      <div
        className="prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: data.description }}
      />
      <div>
        <div className="text-xs uppercase font-semibold text-[var(--text-secondary)] tracking-widest mb-2 flex items-center gap-2">
          <Users className="h-3 w-3" />{" "}
          {t("teacher.submissionsCount", {
            count: (data.submissions || []).length,
          })}
        </div>
        {(data.submissions || []).length === 0 ? (
          <div className="text-sm text-[var(--text-muted)]">
            {t("teacher.noSubmissionsYet")}
          </div>
        ) : (
          <div className="space-y-2">
            {(data.submissions || []).map((sub) => (
              <SubmissionRow
                key={sub.id}
                sub={sub}
                hid={hid}
                onGraded={() => {
                  refetch();
                  onGraded();
                }}
                maxMarks={data.max_marks}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SubmissionRow({ sub, hid, onGraded, maxMarks, t }) {
  const [marks, setMarks] = useState(sub.marks_obtained ?? "");
  const [feedback, setFeedback] = useState(sub.feedback || "");
  const mut = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.post(`/homework/${hid}/submissions/${sub.id}/grade`, {
          marks_obtained: marks === "" ? null : Number(marks),
          feedback,
        }),
      ),
    onSuccess: () => {
      toast.success(t("teacher.gradedToast"));
      onGraded();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="border border-[var(--subtle)] rounded p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold text-sm">
            {sub.student?.first_name} {sub.student?.last_name}
          </div>
          <div className="text-xs text-[var(--text-muted)]">
            {t("teacher.submittedOn", { date: formatDate(sub.submitted_at) })}
          </div>
        </div>
        {sub.status === "graded" && (
          <Badge className="bg-[var(--success-bg)] text-[var(--success)] border-0">
            <Check className="h-3 w-3 mr-1" /> {t("teacher.graded")}
          </Badge>
        )}
      </div>
      {sub.text && (
        <div className="text-sm mt-2 bg-[var(--bg)] rounded p-2">
          {sub.text}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <Input
          placeholder={t("teacher.marksPlaceholder", { max: maxMarks || "?" })}
          type="number"
          value={marks}
          onChange={(e) => setMarks(e.target.value)}
          className="font-mono-num h-9"
          data-testid={`grade-marks-${sub.id}`}
        />
        <Input
          placeholder={t("teacher.feedback")}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          className="h-9"
        />
      </div>
      <Button
        size="sm"
        onClick={() => mut.mutate()}
        disabled={mut.isPending}
        className="mt-2 bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white h-8"
        data-testid={`grade-submit-${sub.id}`}
      >
        {mut.isPending ? t("teacher.saving") : t("teacher.saveGrade")}
      </Button>
    </div>
  );
}
